import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { requestInteractiveDesktopTour } from '../../tutorial/tourBridge';
import { PLATFORM_TOUR_STEPS, ROUTE_TIPS } from '../../constants/productTutorial';
import {
  tutorialGet,
  tutorialSet,
  tutorialRemove,
  tutorialClearPrefix,
  tutorialSuppressAll,
  isTutorialSuppressed,
  TUTORIAL_STORAGE_PREFIX,
} from '../../utils/tutorialStorage';

const KEY_WELCOME = 'welcome_offer';

const SUPPRESS_WELCOME_PATHS = ['/onboarding', '/account-locked'];

function findRouteTip(pathname) {
  const row = ROUTE_TIPS.find((t) => t.test(pathname));
  return row || null;
}

function shouldSuppressRouteTips(pathname) {
  return pathname === '/onboarding' || pathname === '/account-locked';
}

export default function ProductTutorial() {
  const theme = useTheme();
  const location = useLocation();
  const pathname = location.pathname || '/';

  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    defaultMatches: false,
  });
  const welcomeDelay = prefersReducedMotion ? 120 : 800;
  const routeTipDelay = prefersReducedMotion ? 120 : 500;

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [routeTip, setRouteTip] = useState(null);
  const [helpAnchor, setHelpAnchor] = useState(null);

  const routeTipTimer = useRef(null);
  const tourBlockingRef = useRef(false);
  const welcomeOpenRef = useRef(false);
  const tourOpenRef = useRef(false);
  welcomeOpenRef.current = welcomeOpen;
  tourOpenRef.current = tourOpen;

  const openTour = useCallback(() => {
    tutorialSet(KEY_WELCOME);
    tourBlockingRef.current = true;
    setTourStep(0);
    setTourOpen(true);
    setWelcomeOpen(false);
  }, []);

  const finishTour = useCallback(() => {
    tutorialSet(KEY_WELCOME);
    setTourOpen(false);
    tourBlockingRef.current = false;
  }, []);

  const dismissWelcome = useCallback(() => {
    tutorialSet(KEY_WELCOME);
    setWelcomeOpen(false);
  }, []);

  useEffect(() => {
    const welcomeFullKey = `${TUTORIAL_STORAGE_PREFIX}${KEY_WELCOME}`;
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) return;
      if (e.key !== welcomeFullKey || !e.newValue) return;
      setWelcomeOpen(false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /** Welcome is gated by `KEY_WELCOME` in storage — only shown until the user completes or dismisses it. */
  useEffect(() => {
    if (tutorialGet(KEY_WELCOME)) {
      setWelcomeOpen(false);
      return undefined;
    }
    if (SUPPRESS_WELCOME_PATHS.includes(pathname)) {
      return undefined;
    }
    const t = setTimeout(() => {
      if (tutorialGet(KEY_WELCOME)) return;
      if (SUPPRESS_WELCOME_PATHS.includes(pathnameRef.current)) return;
      setWelcomeOpen(true);
    }, welcomeDelay);
    return () => clearTimeout(t);
  }, [pathname, welcomeDelay]);

  useEffect(() => {
    if (routeTipTimer.current) {
      clearTimeout(routeTipTimer.current);
      routeTipTimer.current = null;
    }
    setRouteTip(null);

    if (shouldSuppressRouteTips(pathname)) return;

    const tip = findRouteTip(pathname);
    if (!tip) return;
    if (tutorialGet(tip.storageKey)) return;
    if (!tutorialGet(KEY_WELCOME)) return;
    if (tourBlockingRef.current || tourOpenRef.current || welcomeOpenRef.current) return;

    const scheduledPath = pathname;
    const scheduledStorageKey = tip.storageKey;

    routeTipTimer.current = setTimeout(() => {
      if (pathnameRef.current !== scheduledPath) return;
      if (tourBlockingRef.current || tourOpenRef.current || welcomeOpenRef.current) return;
      if (!tutorialGet(KEY_WELCOME)) return;
      if (tutorialGet(scheduledStorageKey)) return;
      const tipNow = findRouteTip(pathnameRef.current);
      if (!tipNow || tipNow.storageKey !== scheduledStorageKey) return;
      setRouteTip(tipNow);
    }, routeTipDelay);

    return () => {
      if (routeTipTimer.current) clearTimeout(routeTipTimer.current);
    };
  }, [pathname, tourOpen, welcomeOpen, routeTipDelay]);

  const dismissRouteTip = () => {
    if (routeTip) tutorialSet(routeTip.storageKey);
    setRouteTip(null);
  };

  const tourNext = () => {
    if (tourStep >= PLATFORM_TOUR_STEPS.length - 1) {
      finishTour();
    } else {
      setTourStep((s) => s + 1);
    }
  };

  const tourBack = () => setTourStep((s) => Math.max(0, s - 1));

  const resetAllTips = useCallback(() => {
    tutorialClearPrefix();
    setHelpAnchor(null);
    tourBlockingRef.current = false;
    setTourOpen(false);
    setTourStep(0);
    setRouteTip(null);
    if (routeTipTimer.current) {
      clearTimeout(routeTipTimer.current);
      routeTipTimer.current = null;
    }
    setWelcomeOpen(true);
  }, []);

  const step = PLATFORM_TOUR_STEPS[tourStep];

  if (pathname === '/account-locked' || isTutorialSuppressed()) {
    return null;
  }

  const stopAllTutorials = () => {
    tutorialSuppressAll();
    setWelcomeOpen(false);
    setTourOpen(false);
    setRouteTip(null);
    setHelpAnchor(null);
  };

  const fabStyle = {
    position: 'fixed',
    right: 'max(24px, env(safe-area-inset-right, 0px))',
    bottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
    zIndex: theme.zIndex.modal - 1,
  };

  return (
    <>
      <Fab
        color="primary"
        size="small"
        aria-label="Help and tutorials"
        aria-haspopup="true"
        aria-expanded={Boolean(helpAnchor)}
        style={fabStyle}
        onClick={(e) => setHelpAnchor(e.currentTarget)}
      >
        <HelpOutlineIcon />
      </Fab>
      <Menu
        id="ace-tutorial-help-menu"
        anchorEl={helpAnchor}
        open={Boolean(helpAnchor)}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setHelpAnchor(null);
            openTour();
          }}
        >
          Platform tour ({PLATFORM_TOUR_STEPS.length} steps)
        </MenuItem>
        <MenuItem
          disabled={!isDesktop}
          title={!isDesktop ? 'Use a wider desktop layout for the click-along tour' : ''}
          onClick={() => {
            setHelpAnchor(null);
            requestInteractiveDesktopTour();
          }}
        >
          Hands-on walkthrough (click-along)
        </MenuItem>
        <MenuItem
          disabled={!findRouteTip(pathname)}
          onClick={() => {
            setHelpAnchor(null);
            const tip = findRouteTip(pathname);
            if (tip) {
              tutorialRemove(tip.storageKey);
              setRouteTip(tip);
            }
          }}
        >
          Tip for this page
        </MenuItem>
        <MenuItem
          onClick={() => {
            setHelpAnchor(null);
            resetAllTips();
          }}
        >
          Reset all tips & tour
        </MenuItem>
      </Menu>

      <Dialog
        open={welcomeOpen}
        onClose={dismissWelcome}
        maxWidth="sm"
        fullWidth
        aria-labelledby="ace-welcome-title"
        aria-describedby="ace-welcome-desc"
      >
        <DialogTitle id="ace-welcome-title">New here?</DialogTitle>
        <DialogContent>
          <Typography id="ace-welcome-desc" variant="body1" gutterBottom>
            Take a short guided tour to learn where quotations, jobs, barcodes, and settings live — and how the sidebar and header work together.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopAllTutorials} color="default" size="small" style={{ marginRight: 'auto' }}>
            Don't show again
          </Button>
          <Button onClick={dismissWelcome} color="default">
            Maybe later
          </Button>
          <Button
            onClick={() => {
              dismissWelcome();
              requestInteractiveDesktopTour();
            }}
            color="primary"
            variant="outlined"
            disabled={!isDesktop}
            title={!isDesktop ? 'Resize the window for the click-along tour' : ''}
          >
            Hands-on walkthrough
          </Button>
          <Button onClick={openTour} color="primary" variant="contained">
            Start tour
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tourOpen}
        onClose={finishTour}
        maxWidth="sm"
        fullWidth
        aria-labelledby="ace-tour-title"
        aria-describedby="ace-tour-desc"
      >
        <DialogTitle id="ace-tour-title">
          Platform tour ({tourStep + 1} / {PLATFORM_TOUR_STEPS.length})
        </DialogTitle>
        <DialogContent key={tourStep}>
          <Typography variant="h6" component="h2" gutterBottom>
            {step.title}
          </Typography>
          <Typography
            id="ace-tour-desc"
            variant="body2"
            color="textSecondary"
            component="div"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {step.body}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopAllTutorials} color="default" size="small" style={{ marginRight: 'auto' }}>
            Don't show again
          </Button>
          <Button onClick={finishTour} color="default">
            Skip tour
          </Button>
          {tourStep > 0 && (
            <Button onClick={tourBack} color="default">
              Back
            </Button>
          )}
          <Button onClick={tourNext} color="primary" variant="contained">
            {tourStep >= PLATFORM_TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(routeTip)}
        onClose={dismissRouteTip}
        maxWidth="sm"
        fullWidth
        aria-labelledby="ace-route-tip-title"
        aria-describedby="ace-route-tip-desc"
      >
        {routeTip && (
          <>
            <DialogTitle id="ace-route-tip-title">{routeTip.title}</DialogTitle>
            <DialogContent>
              <Typography
                id="ace-route-tip-desc"
                variant="body2"
                color="textSecondary"
                component="div"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {routeTip.body}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={stopAllTutorials} color="default" size="small" style={{ marginRight: 'auto' }}>
                Don't show again
              </Button>
              <Button onClick={dismissRouteTip} color="primary" variant="contained">
                Got it
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
